import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, Button, Table, TableBody, TableRow, TableCell, Alert } from '@mui/material';
import axios from 'axios';
import { toast } from 'react-toastify';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

const ClientReview = () => {
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('client_token');
  const tenantId = localStorage.getItem('client_tenant_id');

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchTenant = async () => {
    if (!token || !tenantId) {
      navigate('/client/login');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/client/tenant/${tenantId}`, { headers });
      setTenant(response.data.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load tenant details');
      navigate('/client/login');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenant();
  }, []);

  const approve = async () => {
    try {
      await axios.post(`${API_BASE_URL}/auth/client/approve/${tenantId}`, {}, { headers });
      toast.success('Tenant details approved successfully');
      fetchTenant();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Approval failed');
    }
  };

  const approveDeletion = async () => {
    try {
      await axios.post(`${API_BASE_URL}/auth/client/delete-approve/${tenantId}`, {}, { headers });
      toast.success('Tenant deletion approved and completed');
      localStorage.removeItem('client_token');
      localStorage.removeItem('client_tenant_id');
      navigate('/client/login');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Tenant deletion approval failed');
    }
  };

  if (loading) {
    return <Box sx={{ p: 4 }}><Typography>Loading...</Typography></Box>;
  }

  if (!tenant) return null;

  return (
    <Box sx={{ p: 2, maxWidth: 900, mx: 'auto' }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>Review Tenant Details</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Confirm your details to move to ERPForC verification.
        </Typography>

        {tenant.status === 'client_approved' ? (
          <Alert severity="success" sx={{ mb: 2 }}>You already approved this tenant.</Alert>
        ) : null}
        {tenant.deletion_approval_status === 'pending_client_approval' ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Tenant deletion is requested by ERPForC and requires your approval.
          </Alert>
        ) : null}

        <Table>
          <TableBody>
            <TableRow><TableCell>Tenant ID</TableCell><TableCell>{tenant.tenant_id}</TableCell></TableRow>
            <TableRow><TableCell>Tenant Name</TableCell><TableCell>{tenant.tenant_name}</TableCell></TableRow>
            <TableRow><TableCell>Company</TableCell><TableCell>{tenant.company_name || '-'}</TableCell></TableRow>
            <TableRow><TableCell>Address</TableCell><TableCell>{tenant.company_address || '-'}</TableCell></TableRow>
            <TableRow><TableCell>Contact Email</TableCell><TableCell>{tenant.contact_email}</TableCell></TableRow>
            <TableRow><TableCell>Contact Phone</TableCell><TableCell>{tenant.contact_phone || '-'}</TableCell></TableRow>
            <TableRow><TableCell>Office Direct</TableCell><TableCell>{tenant.office_direct_number || '-'}</TableCell></TableRow>
            <TableRow><TableCell>Website</TableCell><TableCell>{tenant.website || '-'}</TableCell></TableRow>
            <TableRow><TableCell>Environment</TableCell><TableCell>{tenant.environment === 'custom' ? `custom (${tenant.custom_environment_name || ''})` : tenant.environment}</TableCell></TableRow>
            <TableRow><TableCell>Status</TableCell><TableCell>{tenant.status}</TableCell></TableRow>
            <TableRow><TableCell>Deletion Approval Status</TableCell><TableCell>{tenant.deletion_approval_status || 'not_requested'}</TableCell></TableRow>
          </TableBody>
        </Table>

        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={approve}
            disabled={tenant.status !== 'submitted'}
          >
            Approve Details
          </Button>
          {tenant.deletion_approval_status === 'pending_client_approval' && (
            <Button
              variant="contained"
              color="error"
              onClick={approveDeletion}
            >
              Approve Tenant Deletion
            </Button>
          )}
          <Button onClick={() => { localStorage.removeItem('client_token'); localStorage.removeItem('client_tenant_id'); navigate('/client/login'); }}>
            Sign Out
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ClientReview;

